import { useCallback, useMemo } from 'react';
import {
  ButtonVariants,
  Form,
  FormSizes,
  Modal,
  ModalSizes,
  Typography,
  TypographyColors,
  TypographyVariants,
  TypographyWeights,
} from '@carboledger/ui';
import { ReferenceIdField, TextField } from '@/components/common';
import { useTranslations } from '@/hooks';
import { isFormDirty } from '@/lib/utils';
import {
  useCreateCustomerMutation,
  useGetCustomerQuery,
  useUpdateCustomerMutation,
} from '../api/customersApi';
import {
  CUSTOMER_FIELDS,
  EMPTY_CUSTOMER_FORM,
  ORGANIZATION_NAME_MAX_LENGTH,
  REQUIRED_CUSTOMER_FIELDS,
} from '../constants/customers.constants';
import { customerValidation } from '../customersValidation';
import { hasRequiredFields } from '../utils/customersUtils';
import styles from './CustomerFormModal.module.scss';

/**
 * Props:
 *   title  the section heading
 */
const SectionHeading = ({ title }) => (
  <div className={styles.sectionHeader}>
    <Typography
      variant={TypographyVariants.label}
      weight={TypographyWeights.semibold}
      color={TypographyColors.secondary}
    >
      {title}
    </Typography>
  </div>
);

/**
 * Props:
 *   values    the live form values — the name counter reads them
 *   isEdit    locks the customer ID — a saved customer keeps it
 */
const CustomerFields = ({ values, isEdit }) => {
  const t = useTranslations();

  const nameLength = (values[CUSTOMER_FIELDS.organizationName] ?? '').length;
  const nameHint = t('FORM_CHARACTER_COUNT', {
    count: nameLength,
    max: ORGANIZATION_NAME_MAX_LENGTH,
  });

  return (
    <div className={styles.fields}>
      <SectionHeading title={t('SECTION_CUSTOMER_DETAILS')} />

      <TextField
        required
        name={CUSTOMER_FIELDS.organizationName}
        label={t('FORM_ORGANIZATION_NAME')}
        placeholder={t('FORM_ORGANIZATION_NAME_PLACEHOLDER')}
        rules={customerValidation.organizationName}
        hint={nameHint}
      />

      <ReferenceIdField
        label={t('FIELD_CUSTOMER_ID')}
        prefixLabel={t('CUSTOMERS_FORM_ID_PREFIX')}
        numberLabel={t('CUSTOMERS_FORM_ID_NUMBER')}
        rules={customerValidation.idNumber}
        readOnly={isEdit}
      />

      <Form.Row columns={2}>
        <TextField
          name={CUSTOMER_FIELDS.primaryEmail}
          label={t('FORM_PRIMARY_EMAIL_OPTIONAL')}
          placeholder={t('FORM_PRIMARY_EMAIL_PLACEHOLDER')}
          rules={customerValidation.email}
        />
        <TextField
          name={CUSTOMER_FIELDS.primaryContactName}
          label={t('FORM_PRIMARY_CONTACT_NAME_OPTIONAL')}
          placeholder={t('FORM_PRIMARY_CONTACT_NAME_PLACEHOLDER')}
        />
      </Form.Row>

      <SectionHeading title={t('SECTION_CUSTOMER_ADDRESS_DETAILS')} />

      <TextField
        required
        name={CUSTOMER_FIELDS.addressLine1}
        label={t('FORM_ADDRESS_LINE_1')}
        placeholder={t('FORM_ADDRESS_LINE_1_PLACEHOLDER')}
      />
      <TextField
        name={CUSTOMER_FIELDS.addressLine2}
        label={t('FORM_ADDRESS_LINE_2')}
        placeholder={t('FORM_ADDRESS_LINE_2_PLACEHOLDER')}
      />

      <Form.Row columns={2}>
        <TextField
          required
          name={CUSTOMER_FIELDS.city}
          label={t('FORM_CITY_NAME')}
          placeholder={t('FORM_CITY_NAME_PLACEHOLDER')}
        />
        <TextField
          required
          name={CUSTOMER_FIELDS.country}
          label={t('FORM_COUNTRY_NAME')}
          placeholder={t('FORM_COUNTRY_NAME_PLACEHOLDER')}
        />
      </Form.Row>

      <Form.Row columns={2}>
        <TextField
          required
          name={CUSTOMER_FIELDS.zipCode}
          label={t('FORM_ZIP_CODE')}
          placeholder={t('FORM_ZIP_CODE_PLACEHOLDER')}
        />
      </Form.Row>
    </div>
  );
};

/**
 * Props:
 *   open        whether the modal is shown
 *   onClose     close handler (✕ / Cancel)
 *   customerId  the customer being edited; omit to add a new one
 */
export const CustomerFormModal = ({ open, onClose, customerId }) => {
  const t = useTranslations();
  const [createCustomer, { isLoading: isCreating }] = useCreateCustomerMutation();
  const [updateCustomer, { isLoading: isUpdating }] = useUpdateCustomerMutation();

  const isEdit = Boolean(customerId);
  const saving = isCreating || isUpdating;

  // The list row carries no address, so edit always prefills from the detail read.
  const { data: customer } = useGetCustomerQuery(customerId, { skip: !open || !isEdit });

  const initialValues = useMemo(
    () => customer?.formValues ?? EMPTY_CUSTOMER_FORM,
    [customer?.formValues],
  );

  const handleSubmit = useCallback(
    async (values) => {
      const result = isEdit
        ? await updateCustomer({ id: customerId, values })
        : await createCustomer(values);
      if (result.error) return;
      onClose();
    },
    [isEdit, customerId, createCustomer, updateCustomer, onClose],
  );

  if (!open || (isEdit && !customer)) return null;

  return (
    <Form
      size={FormSizes.medium}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      {({ values, isValid, submit }) => {
        // Edit saves only a real change; add saves only a complete form.
        const canSave =
          isValid &&
          hasRequiredFields(values, REQUIRED_CUSTOMER_FIELDS) &&
          (!isEdit || isFormDirty(values, initialValues));

        return (
          <Modal
            open
            size={ModalSizes.medium}
            title={isEdit ? t('CUSTOMERS_EDIT_TITLE') : t('CUSTOMERS_ADD')}
            onClose={onClose}
            buttons={[
              { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
              {
                label: t('COMMON_SAVE'),
                variant: ButtonVariants.primary,
                disabled: saving || !canSave,
                onClick: submit,
              },
            ]}
          >
            <CustomerFields values={values} isEdit={isEdit} />
          </Modal>
        );
      }}
    </Form>
  );
};
